import React, { useCallback, useMemo, useState } from 'react';
import { useNotify, useRefresh, usePermissions } from 'react-admin';
import { gql } from 'apollo-boost';
import { Box, Card, CardContent, Typography, TextField, Table, TableHead, TableRow, TableCell, TableBody, Checkbox, Button, Stack, Chip, CircularProgress, MenuItem, Switch, FormControlLabel } from '@mui/material';
import { client } from '../helpers/apolloClient';
import { buildXlsxBlob } from '../helpers/xlsx';
const MONTH_QUERY = gql`
    query paymentMonth($month: String!) {
        paymentMonth(month: $month) {
            id month label status approvedAt paidAt
            rows {
                record registrationId name fatherName amount extraAmount included note
                accountHolderName bankName accountNumber blocked bankDetailsChanged
            }
        }
    }
`;

const SET_ROW = gql`
    mutation setPayoutRow($record: SetPayoutRowInput!) {
        setPayoutRow(record: $record) { updated }
    }
`;

const REFRESH = 'Refresh';

// What a row pays: its amount for the month plus any one-off extra. Mirrors
// rowTotal on the server so the totals on screen match the bank file.
const rowTotal = row => (row.amount || 0) + (row.extraAmount || 0);

const blockedLabel = reason => ({
    unlinked: 'No account holder',
    'no-bank-account': 'No bank account',
    'choose-bank-account': 'Choose an account',
}[reason] || reason);

// The tick list. Rows are the payout, so everything that decides money happens
// here: untick someone, adjust an amount for this month alone, and say why.
export const PaymentMonthScreen = ({ month, status }) => {
    const notify = useNotify();
    const refresh = useRefresh();
    const { permissions } = usePermissions();
    const canApprove = permissions.includes('financeAdmin');

    const [rows, setRows] = useState(null);
    const [search, setSearch] = useState('');
    const [holderSearch, setHolderSearch] = useState('');
    const [grouped, setGrouped] = useState(true);
    const [busy, setBusy] = useState(false);
    const [reloadKey, setReloadKey] = useState(0);

    const load = useCallback(() => {
        setBusy(true);
        client.query({ query: MONTH_QUERY, variables: { month }, fetchPolicy: 'network-only' })
            .then(({ data }) => setRows(data.paymentMonth.rows))
            .catch(() => notify('Could not load the month', { type: 'warning' }))
            .finally(() => setBusy(false));
    }, [month, notify]);

    // Refresh only reloads. Re-sync is a separate, explicit action because it
    // changes the list.
    React.useEffect(load, [load, reloadKey]);

    const isDraft = status === 'Draft';

    const mutate = (variables) =>
        client.mutate({ mutation: SET_ROW, variables: { record: { month, ...variables } } })
            .then(() => load())
            .catch(e => notify(e.message, { type: 'warning' }));

    const setField = (row, field, value) => mutate({ record: row.record, [field]: value });

    // One call per row, then a single reload: mutating through `mutate` would
    // reload the whole month after every single tick. Blocked rows are skipped
    // up front rather than asked of the server, which refuses them one at a
    // time - a row that cannot be paid stays unticked with its reason showing.
    const setAllIncluded = async included => {
        if (!rows) return;
        const targets = included ? rows.filter(r => !r.included) : rows.filter(r => r.included);
        const changeable = included ? targets.filter(r => !r.blocked) : targets;
        if (changeable.length === 0) {
            notify(included ? 'Nothing left to tick' : 'Nothing is ticked', { type: 'info' });
            return;
        }

        setBusy(true);
        try {
            const results = await Promise.allSettled(changeable.map(row =>
                client.mutate({
                    mutation: SET_ROW,
                    variables: { record: { month, record: row.record, included } },
                })
            ));
            const failed = results.filter(r => r.status === 'rejected').length;
            const done = results.length - failed;
            const stillBlocked = included ? targets.length - changeable.length : 0;

            let message = included
                ? `Ticked ${done} of ${targets.length} `
                : `Unticked ${done} `;
            if (stillBlocked) message += `, ${stillBlocked} blocked rows left unticked`;
            notify(message + (failed ? `, ${failed} failed` : ''), failed ? { type: 'warning' } : undefined);

            setReloadKey(k => k + 1);
            refresh();
        } finally {
            setBusy(false);
        }
    };

    // One field matches the beneficiary or their ET number; the other matches
    // who they are paid through or the account itself. Applied together, each
    // empty field constrains nothing.
    const visible = useMemo(() => {
        if (!rows) return [];
        let result = rows;
        const term = search.trim().toLowerCase();
        if (term) {
            result = result.filter(r => `${r.registrationId || ''} ${r.name || ''} ${r.fatherName || ''}`
                .toLowerCase().includes(term));
        }
        const holderTerm = holderSearch.trim().toLowerCase();
        if (holderTerm) {
            result = result.filter(r => `${r.accountHolderName || ''} ${r.bankName || ''} ${r.accountNumber || ''}`
                .toLowerCase().includes(holderTerm));
        }
        return result;
    }, [rows, search, holderSearch]);

    // Grouping is for settling up with one holder at a time: their rows, with
    // their banks, under one heading and one subtotal. Nobody linked sits last.
    const groups = useMemo(() => {
        if (!grouped) return [];
        const byHolder = new Map();
        for (const row of visible) {
            const key = row.accountHolder || row.accountHolderName || '';
            if (!byHolder.has(key)) byHolder.set(key, { name: row.accountHolderName, rows: [] });
            byHolder.get(key).rows.push(row);
        }
        return [...byHolder.entries()]
            .sort(([aKey, a], [bKey, b]) => {
                if (!a.name) return 1;
                if (!b.name) return -1;
                return (a.name || '').localeCompare(b.name || '');
            });
    }, [visible, grouped]);

    // Most beneficiaries share one standing allowance, so the month's normal
    // amount is simply its most common value - the mode. Only a clear majority
    // counts as normal; a month with no dominant amount flags nothing, rather
    // than painting every row orange.
    const normalAmount = useMemo(() => {
        if (!rows || rows.length === 0) return null;
        const counts = new Map();
        for (const row of rows) {
            const amount = row.amount || 0;
            counts.set(amount, (counts.get(amount) || 0) + 1);
        }
        let best = null;
        let bestCount = 0;
        for (const [amount, count] of counts) {
            if (count > bestCount) {
                best = amount;
                bestCount = count;
            }
        }
        return bestCount * 2 > rows.length ? best : null;
    }, [rows]);

    // A row paying something other than the normal amount is worth a second
    // look - it is flagged in orange so the eye lands on it.
    const isOutlier = row => normalAmount !== null && (row.amount || 0) !== normalAmount;

    const ticked = useMemo(() => (rows || []).filter(r => r.included), [rows]);

    // Per-bank totals, so each file can be checked before it is taken.
    const byBank = useMemo(() => {
        const totals = new Map();
        ticked.forEach(r => {
            const key = r.bankName || 'unresolved';
            const line = totals.get(key) || { count: 0, amount: 0 };
            line.count += 1;
            line.amount += rowTotal(r);
            totals.set(key, line);
        });
        return [...totals.entries()].sort(([a], [b]) => a.localeCompare(b));
    }, [ticked]);

    const exportFile = async (bankName) => {
        try {
            const { data } = await client.mutate({
                mutation: gql`mutation e($record: ExportBankFileInput!) {
                    exportBankFile(record: $record) { filename contents }
                }`,
                variables: { record: { month, bankName } },
            });
            const { filename, contents } = data.exportBankFile;

            const url = URL.createObjectURL(new Blob([contents], { type: 'text/csv;charset=utf-8' }));
            const link = document.createElement('a');
            link.href = url;
            link.download = filename;
            link.click();
            URL.revokeObjectURL(url);

            refresh();
        } catch (e) {
            notify(e.message, { type: 'warning' });
        }
    };

    // The bank's transfer sheet: one row per account holder for the chosen bank,
    // their account and the sum of their ticked rows. Built from the rows already
    // on screen rather than a second server round trip, and written as a real
    // .xlsx so it opens as a spreadsheet rather than a wall of text.
    const downloadBankTransfer = bankName => {
        const byAccount = new Map();
        for (const row of rows || []) {
            if (!row.included || row.bankName !== bankName) continue;

            const key = row.accountHolder || row.accountHolderName || row.accountNumber;
            const line = byAccount.get(key) || {
                name: row.accountHolderName || '',
                accountNumber: row.accountNumber || '',
                amount: 0,
            };
            line.amount += rowTotal(row);
            byAccount.set(key, line);
        }

        const sheet = [
            ['Name', 'Account Number', 'Total Amount'],
            ...[...byAccount.values()]
                .sort((a, b) => a.name.localeCompare(b.name))
                .map(line => [line.name, line.accountNumber, line.amount]),
        ];

        const blob = buildXlsxBlob(sheet, `${month} ${bankName}`.slice(0, 31), [42, 26, 16]);
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `${month}-${bankName.replace(/[^A-Za-z0-9]+/g, '-')}-bank-transfer.xlsx`;
        link.click();
        URL.revokeObjectURL(url);
    };

    const transition = async (label, mutation, variables) => {
        try {
            await client.mutate({ mutation, variables });
            notify(label);
            setReloadKey(k => k + 1);
            refresh();
        } catch (e) {
            notify(e.message, { type: 'warning' });
        }
    };

    // The row table is rendered plain, or once under each holder's heading when
    // the group switch is on. The heading carries the holder's subtotal so the
    // group can be settled on its own numbers without opening anything.
    const renderRows = tableRows => (
        <Table size="small">
            <TableHead>
                <TableRow>
                    <TableCell>Pay</TableCell>
                    <TableCell>ET number</TableCell>
                    <TableCell>Beneficiary</TableCell>
                    <TableCell>Account holder</TableCell>
                    <TableCell>Bank</TableCell>
                    <TableCell align="right">Amount</TableCell>
                    <TableCell align="right">Extra</TableCell>
                    <TableCell>Note</TableCell>
                </TableRow>
            </TableHead>
            <TableBody>
                {tableRows.map(row => (
                    <TableRow
                        key={row.record}
                        sx={{
                            opacity: row.included ? 1 : 0.55,
                            ...(isOutlier(row) && { bgcolor: 'warning.light' }),
                        }}
                    >
                        <TableCell>
                            <Checkbox
                                checked={row.included}
                                disabled={!isDraft || !!row.blocked}
                                onChange={e => setField(row, 'included', e.target.checked)}
                            />
                        </TableCell>
                        <TableCell>{row.registrationId}</TableCell>
                        <TableCell>{`${row.name || ''} ${row.fatherName || ''}`}</TableCell>
                        <TableCell>
                            {row.accountHolderName || <Chip size="small" label="Not linked" color="warning" />}
                            {row.blocked && (
                                <Chip size="small" label={blockedLabel(row.blocked)} color="warning" sx={{ ml: 1 }} />
                            )}
                            {row.bankDetailsChanged && (
                                <Chip size="small" label="Bank details changed" color="error" sx={{ ml: 1 }} />
                            )}
                        </TableCell>
                        <TableCell>
                            {row.bankName ? `${row.bankName} ${row.accountNumber}` : '—'}
                        </TableCell>
                        <TableCell align="right">
                            <TextField
                                size="small" type="number" variant="standard"
                                defaultValue={row.amount}
                                disabled
                            />
                            {isOutlier(row) && (
                                <Chip size="small" color="warning" label={`Usually ${normalAmount}`} sx={{ mt: 0.5 }} />
                            )}
                        </TableCell>
                        <TableCell align="right">
                            <TextField
                                size="small" type="number" variant="standard"
                                defaultValue={row.extraAmount || 0}
                                disabled={!isDraft}
                                onBlur={e => {
                                    const next = parseInt(e.target.value, 10);
                                    const value = Number.isNaN(next) ? 0 : next;
                                    if (value !== (row.extraAmount || 0)) {
                                        setField(row, 'extraAmount', value);
                                    }
                                }}
                            />
                        </TableCell>
                        <TableCell>
                            <TextField
                                size="small" variant="standard" fullWidth
                                defaultValue={row.note || ''}
                                disabled={!isDraft}
                                placeholder={row.included ? 'Reaches the bank' : 'Why not this month?'}
                                onBlur={e => setField(row, 'note', e.target.value)}
                            />
                        </TableCell>
                    </TableRow>
                ))}
            </TableBody>
        </Table>
    );

    return (
        <Card>
            <CardContent>
                <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2, flexWrap: 'wrap' }}>
                    <TextField
                        size="small"
                        label="Search by ET number or name"
                        value={search}
                        onChange={e => setSearch(e.target.value)}
                    />
                    <TextField
                        size="small"
                        label="Search by account holder or account number"
                        value={holderSearch}
                        onChange={e => setHolderSearch(e.target.value)}
                    />
                    <FormControlLabel
                        control={
                            <Switch
                                size="small"
                                checked={grouped}
                                onChange={e => setGrouped(e.target.checked)}
                            />
                        }
                        label="Group by account holder"
                    />
                    <Box sx={{ flexGrow: 1 }} />
                    <Button size="small" onClick={() => setReloadKey(k => k + 1)}>{REFRESH}</Button>
                    {isDraft && (
                        <>
                            <Button size="small" disabled={busy} onClick={() => setAllIncluded(true)}>Tick all</Button>
                            <Button size="small" disabled={busy} onClick={() => setAllIncluded(false)}>Untick all</Button>
                        </>
                    )}
                    {isDraft && (
                        <Button size="small" onClick={async () => {
                            // Applied, not just previewed: a preview that never
                            // applies left rows generated against stale holder
                            // links - unlinked, without an account, untickable -
                            // on screen even after the linking had happened.
                            const { data } = await client.query({
                                query: gql`query p($record: PaymentMonthInput!) {
                                    previewPaymentPeriodResync(record: $record) {
                                        added { registrationId } removedCount heldForReviewCount revaluedCount
                                    }
                                }`,
                                variables: { record: { month } },
                                fetchPolicy: 'network-only',
                            });
                            const plan = data.previewPaymentPeriodResync;

                            await client.mutate({
                                mutation: gql`mutation r($record: PaymentMonthInput!) {
                                    resyncPaymentPeriod(record: $record) { month }
                                }`,
                                variables: { record: { month } },
                            });
                            notify(`Re-synced: added ${plan.added.length}, removed ${plan.removedCount}, held ${plan.heldForReviewCount}, updated amounts ${plan.revaluedCount}`);
                        }}>Re-sync</Button>
                    )}
                    {isDraft && canApprove && (
                        <Button size="small" variant="contained" onClick={() => transition(
                            'Month approved',
                            gql`mutation a($month: String!) { approvePaymentPeriod(month: $month) { status } }`,
                            { month },
                        )}>Approve</Button>
                    )}
                    {!isDraft && canApprove && status === 'Approved' && (
                        <Button size="small" variant="contained" onClick={() => transition(
                            'Month marked paid',
                            gql`mutation p($month: String!) { markPaymentPeriodPaid(month: $month) { status } }`,
                            { month },
                        )}>Mark paid</Button>
                    )}
                    {!isDraft && canApprove && (
                        <Button size="small" onClick={() => {
                            const reason = window.prompt('Why is this month being reopened?');
                            if (!reason) return;
                            return transition('Month reopened', gql`
                                mutation r($record: ReopenPaymentPeriodInput!) {
                                    reopenPaymentPeriod(record: $record) { status }
                                }`, { record: { month, reason } });
                        }}>Reopen</Button>
                    )}
                </Stack>

                {byBank.length > 0 && (
                    <Stack direction="row" spacing={2} sx={{ mb: 2, flexWrap: 'wrap' }}>
                        {/* The bank file is the committed artifact and stays
                            behind approval. The Excel transfer is a plain export
                            of what is ticked, so it is offered while the month is
                            still being prepared. */}
                        {!isDraft && (
                            <TextField
                                select size="small" label="Download bank file"
                                sx={{ minWidth: 220 }}
                                value=""
                                onChange={e => e.target.value && exportFile(e.target.value)}
                            >
                                {byBank.filter(([bank]) => bank !== 'unresolved').map(([bank]) => (
                                    <MenuItem key={bank} value={bank}>{bank}</MenuItem>
                                ))}
                            </TextField>
                        )}
                        <TextField
                            select size="small" label="Download bank transfer (Excel)"
                            sx={{ minWidth: 260 }}
                            value=""
                            onChange={e => e.target.value && downloadBankTransfer(e.target.value)}
                        >
                            {byBank.filter(([bank]) => bank !== 'unresolved').map(([bank]) => (
                                <MenuItem key={bank} value={bank}>{bank}</MenuItem>
                            ))}
                        </TextField>
                    </Stack>
                )}

                <Stack direction="row" spacing={3} sx={{ mb: 1, flexWrap: 'wrap' }}>
                    <Typography variant="body2">
                        Ticked: <strong>{ticked.length}</strong> of {rows ? rows.length : 0}
                    </Typography>
                    <Typography variant="body2">
                        Total: <strong>{ticked.reduce((sum, r) => sum + rowTotal(r), 0)}</strong> ETB
                    </Typography>
                    {byBank.map(([bank, line]) => (
                        <Typography variant="body2" key={bank} color={bank === 'unresolved' ? 'error' : 'text.primary'}>
                            {bank}: <strong>{line.count}</strong> / <strong>{line.amount}</strong>
                        </Typography>
                    ))}
                </Stack>

                {busy && !rows && <CircularProgress size={24} />}

                {grouped ? groups.map(([key, group]) => {
                    const paid = group.rows.filter(r => r.included);
                    return (
                        <Box key={key || 'unlinked'} sx={{ mt: 2 }}>
                            <Stack direction="row" spacing={2} alignItems="baseline" sx={{ flexWrap: 'wrap' }}>
                                <Typography variant="subtitle2">
                                    {group.name || 'No account holder'}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    {paid.length} of {group.rows.length} ticked
                                    {paid.length > 0 ? ` · ${paid.reduce((sum, r) => sum + rowTotal(r), 0)} ETB` : ''}
                                </Typography>
                            </Stack>
                            {renderRows(group.rows)}
                        </Box>
                    );
                }) : renderRows(visible)}
            </CardContent>
        </Card>
    );
};

export default PaymentMonthScreen;